import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageTitle from "../components/PageTitle";

const screenshots = [
  {
    src: "/images/interep/report-forms.png",
    title: "Report form selection",
    caption:
      "Pilots select the report applicable to the occurrence. Forms that require operational flight data are linked to the corresponding vAMSYS PIREP before submission.",
  },
  {
    src: "/images/interep/entry-assessment.png",
    title: "Entry assessment",
    caption:
      "The entry assessment is completed within Interep under controlled attempt, timing and integrity-monitoring conditions.",
  },
];

function InterepFigure({ item, onOpen }) {
  return (
    <figure className="etlb-figure">
      <button
        type="button"
        className="etlb-figure-image"
        onClick={() => onOpen(item)}
        aria-label={`Enlarge ${item.title}`}
      >
        <img src={item.src} alt={item.title} loading="lazy" decoding="async" />
        <span aria-hidden="true">View full size</span>
      </button>
      <figcaption>
        <strong>{item.title}</strong>
        <span>{item.caption}</span>
      </figcaption>
    </figure>
  );
}

export default function InterepOverviewPage() {
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (!selectedImage) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelectedImage(null);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [selectedImage]);

  return (
    <>
      <PageTitle>vWZZ - Interep</PageTitle>
      <div className="page-container etlb-page interep-public-page">
        <header className="etlb-page-header">
          <h1 className="page-title">vWZZ Interep</h1>
          <p className="page-subtitle">
            Interep is the vWZZ internal reporting and entry-assessment system.
            It provides a structured record of operational reports from initial
            submission through review and final decision.
          </p>
        </header>

        <section className="etlb-introduction" aria-labelledby="interep-overview-title">
          <div>
            <h2 id="interep-overview-title">Overview</h2>
            <p>
              Pilots use Interep to submit occurrence-specific reports and to
              communicate with authorised reviewers. Where flight context is
              required, the report is associated with the applicable vAMSYS
              PIREP.
            </p>
            <p>
              Report status, reviewer correspondence and the final decision are
              retained together, providing a consistent record throughout the
              review process.
            </p>
          </div>
          <img
            src="/images/interep/report-forms.png"
            alt="Interep operational report form selection"
          />
        </section>

        <section className="etlb-video-section" aria-labelledby="interep-video-title">
          <div className="etlb-section-copy">
            <h2 id="interep-video-title">Report Submission Example</h2>
            <p>
              The recording demonstrates the Air Safety Report (ASR) and
              Unsuccessful Autoland Report workflows. It shows the dedicated
              form structures and the information recorded during submission.
            </p>
          </div>
          <video
            controls
            playsInline
            preload="metadata"
            poster="/images/interep/report-forms.png"
            aria-label="Interep ASR and Unsuccessful Autoland report demonstration"
          >
            <source src="/video/interep-report-workflows.mp4" type="video/mp4" />
            Your browser does not support embedded MP4 video.
          </video>
        </section>

        <section className="etlb-detail-section">
          <header>
            <div>
              <h2>Reporting and Assessment Interfaces</h2>
              <p>
                Interep separates operational reporting from the pilot entry
                assessment while retaining a consistent authenticated user
                record.
              </p>
            </div>
          </header>
          <div className="etlb-gallery etlb-gallery-2">
            {screenshots.map((item) => (
              <InterepFigure item={item} key={item.src} onOpen={setSelectedImage} />
            ))}
          </div>
        </section>

        <section className="etlb-closing">
          <div>
            <h2>System Use</h2>
            <p>
              Interep is intended exclusively for vWZZ virtual-airline
              operations. Access to reporting, assessment and review functions
              is assigned according to the authenticated user's status and
              responsibilities.
            </p>
          </div>
          <Link className="btn btn-primary" to="/apply">
            Join vWZZ
          </Link>
        </section>

        {selectedImage && (
          <div
            className="etlb-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedImage.title} full-size image`}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setSelectedImage(null);
            }}
          >
            <button
              type="button"
              className="etlb-lightbox-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Close full-size image"
              autoFocus
            >
              Close
            </button>
            <figure>
              <img src={selectedImage.src} alt={selectedImage.title} />
              <figcaption>
                <strong>{selectedImage.title}</strong>
                <span>{selectedImage.caption}</span>
              </figcaption>
            </figure>
          </div>
        )}
      </div>
    </>
  );
}
